/**
 * RemoteBrowserPanel Component
 *
 * Canvas-based browser viewer that displays screenshots from the Remote Browser Service.
 * Captures mouse, keyboard, and scroll events and forwards them to the server.
 *
 * Features:
 * - Real-time screenshot streaming (JPEG @ configurable FPS)
 * - Mouse movement, click, and scroll forwarding
 * - Keyboard input forwarding
 * - Loading and error states
 * - Connection status indicator
 * - Responsive canvas sizing
 * - Mobile keyboard support via hidden proxy input
 */

// ============================================================================
// Mobile Keyboard Bridge
// ============================================================================
// Mobile OS keyboards (iOS/Android) only appear when a LOCAL <input> element
// receives focus. Since the remote browser view is a canvas (not a focusable
// input), we use a hidden proxy input to summon the keyboard and forward
// typed characters to Playwright. This is a standard pattern used by remote
// desktops, browser isolation platforms, and cloud IDEs.
// ============================================================================

import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { useRemoteBrowser } from '@shared/hooks/useRemoteBrowser';
import { RemoteBrowserContextMenu, ContextMenuPosition } from './RemoteBrowserContextMenu';

// ============================================================================
// Mobile Detection Utility
// ============================================================================

/**
 * Detect if the current device is a mobile/touch device.
 * Used to enable the hidden keyboard proxy for mobile text input.
 */
function isMobileDevice(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  // Check for touch capability
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  // Check user agent for mobile patterns (iOS, Android)
  const mobileUA = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
  return hasTouch && mobileUA;
}

// ============================================================================
// Domain-Based Profile ID Generation
// ============================================================================

/**
 * Extract a normalized domain name from a URL to use as a profile ID.
 * This allows login sessions to persist per-domain across browser opens.
 *
 * Examples:
 *   "https://www.google.com/search?q=test" -> "google"
 *   "https://login.pipedrive.com/auth" -> "pipedrive"
 *   "https://outlook.office365.com/mail" -> "outlook-office365"
 *   "http://localhost:3000" -> "localhost"
 *
 * The profile ID is deterministic and based solely on the URL hostname.
 * No user IDs, device fingerprints, or random values are used.
 */
function getDomainProfileId(urlString: string): string {
  try {
    const url = new URL(urlString);
    const hostname = url.hostname.toLowerCase();

    // Remove common prefixes (www, login, auth, app, mail, etc.)
    const withoutPrefix = hostname.replace(
      /^(www\.|login\.|auth\.|app\.|mail\.|m\.|mobile\.)/,
      ''
    );

    // Extract the main domain parts
    const parts = withoutPrefix.split('.');

    // Handle special cases
    if (parts.length === 1) {
      // localhost or single-part hostname
      return parts[0];
    }

    // For most domains, use the second-to-last part (e.g., "google" from "google.com")
    // For longer domains, combine relevant parts (e.g., "outlook-office365" from "outlook.office365.com")
    if (parts.length === 2) {
      return parts[0];
    }

    // For subdomains like "outlook.office365.com", combine meaningfully
    // Skip common TLDs and combine the meaningful parts
    const tlds = ['com', 'org', 'net', 'io', 'co', 'dev', 'app', 'me', 'uk', 'de', 'fr', 'jp'];
    const meaningfulParts = parts.filter((part) => !tlds.includes(part));

    if (meaningfulParts.length >= 2) {
      // Take first two meaningful parts, joined with hyphen
      return meaningfulParts.slice(0, 2).join('-');
    }

    // Fallback: use the first meaningful part or the second-to-last part
    return meaningfulParts[0] || parts[parts.length - 2];
  } catch {
    // If URL parsing fails, return a safe fallback
    return 'default';
  }
}

// ============================================================================
// Types
// ============================================================================

export interface RemoteBrowserPanelProps {
  /** URL to load in the remote browser */
  url: string;
  /** Display title (optional) */
  title?: string;
  /** Callback when navigation occurs */
  onNavigate?: (url: string) => void;
  /** Callback when closed */
  onClose: () => void;
  /** Whether Dynamics theme is active */
  isDynamicsTheme?: boolean;
  /** Streaming frame rate (default: 15, max: 20) */
  fps?: number;
  /** JPEG quality (default: 80) */
  quality?: number;
  /**
   * Whether the panel is visually minimized (hidden via CSS).
   * When true, the browser session stays active but frames are throttled
   * to 1 FPS to save CPU while maintaining the connection.
   */
  isMinimized?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function RemoteBrowserPanel({
  url,
  title,
  onNavigate,
  onClose,
  isDynamicsTheme = false,
  fps = 15,
  quality = 80,
  isMinimized = false,
}: RemoteBrowserPanelProps): React.ReactElement {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const urlInputRef = useRef<HTMLInputElement>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 1920, height: 1080 });
  const [isFocused, setIsFocused] = useState(false);
  // Reusable image object for frame rendering (performance optimization)
  const imageRef = useRef<HTMLImageElement | null>(null);
  // Track frame metrics
  const frameMetricsRef = useRef({ count: 0, lastTime: 0, fps: 0 });
  // Frame queue for requestAnimationFrame rendering
  const pendingFrameRef = useRef<string | null>(null);
  const rafIdRef = useRef<number | null>(null);
  // Track container dimensions for dynamic viewport
  const [containerSize, setContainerSize] = useState<{ width: number; height: number } | null>(null);
  // Mouse move throttling
  const lastMouseMoveRef = useRef<number>(0);
  const MOUSE_MOVE_THROTTLE_MS = 16; // ~60fps max for mouse moves

  // Mobile keyboard bridge: hidden input to summon soft keyboard on touch devices
  // This is necessary because mobile OS keyboards only appear when a LOCAL <input>
  // element receives focus - the canvas alone cannot trigger the keyboard.
  const mobileKeyboardInputRef = useRef<HTMLInputElement>(null);
  const [isMobile] = useState(() => isMobileDevice());
  const lastMobileInputRef = useRef<string>(''); // Track last input value for diff

  // URL input state for navigation
  const [urlInputValue, setUrlInputValue] = useState(url);
  const [isEditingUrl, setIsEditingUrl] = useState(false);

  // Context menu state
  const [contextMenuPosition, setContextMenuPosition] = useState<ContextMenuPosition | null>(null);
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  // Long-press timer for mobile context menu
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const LONG_PRESS_DURATION_MS = 500; // 500ms for long press
  // Track if this is a long press to prevent click from firing
  const isLongPressRef = useRef(false);

  // Ref to track if component is mounted (prevents cleanup on re-renders)
  const isMountedRef = useRef(true);

  // ============================================================================
  // VIEWPORT SIZING - Client panel size is authoritative
  // ============================================================================
  // The viewport calculation does NOT force a fixed aspect ratio.
  // Instead, it uses the actual panel dimensions minus the status bar.
  // This allows the Playwright viewport to match the client display exactly,
  // preventing stretching/squishing of screenshots.
  //
  // The sizing pipeline is:
  // 1. CLIENT PANEL SIZE (source of truth) - measured by ResizeObserver
  // 2. SERVER VIEWPORT SIZE - set via page.setViewportSize() to match client
  // 3. CLIENT CANVAS SIZE - matches panel size, screenshots are drawn 1:1
  // ============================================================================

  const viewport = useMemo(() => {
    if (!containerSize) {
      // Default fallback until container is measured
      return { width: 1280, height: 720 };
    }

    // Use actual container dimensions (accounting for status bar height)
    // DO NOT force a fixed aspect ratio - this causes stretching
    const width = Math.max(320, containerSize.width);
    const height = Math.max(240, containerSize.height - 28); // Status bar is 28px

    // Round to even numbers for better video encoding
    const roundedWidth = Math.floor(width / 2) * 2;
    const roundedHeight = Math.floor(height / 2) * 2;

    return { width: roundedWidth, height: roundedHeight };
  }, [containerSize]);

  const {
    session,
    connectionState,
    error,
    currentFrame,
    isStreaming,
    openBrowser,
    closeBrowser,
    startStreaming,
    stopStreaming,
    sendMouseMove,
    sendMouseClick,
    sendScroll,
    sendKeyDown,
    sendKeyUp,
    sendType,
    navigate: navigateBrowser,
    resizeViewport,
    // Clipboard & context menu actions
    getSelection,
    paste,
    reload,
    selectAll,
  } = useRemoteBrowser();

  // Update URL input when session URL changes
  useEffect(() => {
    if (session?.url) {
      setUrlInputValue(session.url);
    }
  }, [session?.url]);

  // Handle URL refresh
  const handleRefresh = useCallback(() => {
    if (session?.url) {
      navigateBrowser(session.url);
    }
  }, [session?.url, navigateBrowser]);

  // Handle URL navigation
  const handleNavigate = useCallback(() => {
    const trimmedUrl = urlInputValue.trim();
    if (!trimmedUrl) return;

    // Add protocol if missing
    let normalizedUrl = trimmedUrl;
    if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
      normalizedUrl = `https://${trimmedUrl}`;
    }

    navigateBrowser(normalizedUrl);
    onNavigate?.(normalizedUrl);
    setIsEditingUrl(false);
  }, [urlInputValue, navigateBrowser, onNavigate]);

  // Handle URL input key events
  const handleUrlKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleNavigate();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setUrlInputValue(session?.url || url);
        setIsEditingUrl(false);
        canvasRef.current?.focus();
      }
    },
    [handleNavigate, session?.url, url]
  );

  // Handle URL input focus
  const handleUrlFocus = useCallback(() => {
    setIsEditingUrl(true);
  }, []);

  // Handle URL input blur
  const handleUrlBlur = useCallback(() => {
    setIsEditingUrl(false);
    // Reset to current session URL if no navigation happened
    if (session?.url) {
      setUrlInputValue(session.url);
    }
  }, [session?.url]);

  // ==========================================================================
  // Canvas Focus Management
  // ==========================================================================

  // Auto-focus canvas when session is ready and streaming
  useEffect(() => {
    if (session && currentFrame && canvasRef.current) {
      canvasRef.current.focus();
      setIsFocused(true);
    }
  }, [session, currentFrame]);

  // Focus handlers
  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  // ==========================================================================
  // Canvas Sizing & Container Measurement
  // ==========================================================================
  // The canvas size directly matches the container size (minus status bar).
  // DO NOT apply CSS scale() transforms or force fixed aspect ratios.
  // This ensures 1:1 pixel mapping between server screenshots and client display.

  // Debounce timer for viewport resize to avoid flooding server
  const viewportResizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const VIEWPORT_RESIZE_DEBOUNCE_MS = 150;

  useEffect(() => {
    const updateSizes = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();

        // Use actual container dimensions (no forced aspect ratio)
        const width = Math.floor(rect.width);
        const height = Math.floor(rect.height);

        // Update container size for viewport calculation
        setContainerSize({ width, height });

        // Canvas size = container size minus status bar
        // This ensures the canvas fills the available space without stretching
        const canvasWidth = Math.max(320, width);
        const canvasHeight = Math.max(240, height - 28); // 28px status bar

        // Round to even numbers for video encoding
        setCanvasSize({
          width: Math.floor(canvasWidth / 2) * 2,
          height: Math.floor(canvasHeight / 2) * 2,
        });
      }
    };

    updateSizes();
    window.addEventListener('resize', updateSizes);

    // ResizeObserver for panel resizing (e.g., drag-to-resize)
    // Also handles mobile orientation changes
    const resizeObserver = new ResizeObserver(updateSizes);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateSizes);
      resizeObserver.disconnect();
    };
  }, []);

  // ==========================================================================
  // Browser Session
  // ==========================================================================

  // Memoize streaming options to prevent object recreation
  // Enable all performance optimizations by default
  const streamingOptions = useMemo(() => ({
    fps,
    quality,
    useBinary: true,
    adaptiveQuality: true,
  }), [fps, quality]);

  // Display FPS state (updates every second)
  const [displayFps, setDisplayFps] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setDisplayFps(Math.round(frameMetricsRef.current.fps));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Track mounted state for cleanup
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Generate domain-based profile ID for login persistence
  // This ensures the same Playwright profile is used for each domain
  const profileId = useMemo(() => getDomainProfileId(url), [url]);

  // Open browser when URL changes and connected
  // Only run when connectionState changes to 'connected' OR when URL changes
  useEffect(() => {
    if (connectionState === 'connected' && url && !session) {
      openBrowser(url, viewport, profileId);
    }
    // Note: openBrowser is stable (no deps), session check is inside the hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionState, url, viewport, profileId]);

  // Start streaming when session is ready
  useEffect(() => {
    if (session && !isStreaming) {
      startStreaming(streamingOptions);
    }
    // Note: startStreaming is stable (no deps), guards internally
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.pageId, streamingOptions]);

  // ==========================================================================
  // Minimize Frame Throttling
  // ==========================================================================
  // When panel is visually minimized (hidden via CSS), throttle frames to 1 FPS
  // to save CPU while maintaining the browser session connection.
  // When restored, go back to normal FPS.

  useEffect(() => {
    if (!session || !isStreaming) return;

    if (isMinimized) {
      // Throttle to 1 FPS when minimized (just keep connection alive)
      stopStreaming();
      startStreaming({ fps: 1, quality: 30, useBinary: true, adaptiveQuality: false });
    } else {
      // Restore normal streaming options when visible
      stopStreaming();
      startStreaming(streamingOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMinimized]);

  // ==========================================================================
  // Viewport Resize Synchronization
  // ==========================================================================
  // When the panel size changes (ResizeObserver triggers setContainerSize),
  // we need to tell the server to resize the Playwright viewport to match.
  // This is debounced to avoid flooding the server during drag-resize.
  //
  // IMPORTANT: This is bidirectional viewport negotiation:
  // - Client panel size is authoritative (source of truth)
  // - Server viewport follows client via resizeViewport()
  // - Screenshots are captured at viewport size, displayed 1:1 on canvas

  useEffect(() => {
    // Only sync viewport if we have an active session
    if (!session?.pageId || !viewport) return;

    // Clear any pending resize
    if (viewportResizeTimerRef.current) {
      clearTimeout(viewportResizeTimerRef.current);
    }

    // Debounce the resize to avoid flooding the server
    viewportResizeTimerRef.current = setTimeout(() => {
      resizeViewport(viewport.width, viewport.height, window.devicePixelRatio);
    }, VIEWPORT_RESIZE_DEBOUNCE_MS);

    return () => {
      if (viewportResizeTimerRef.current) {
        clearTimeout(viewportResizeTimerRef.current);
      }
    };
  }, [session?.pageId, viewport, resizeViewport]);

  // Cleanup on ACTUAL unmount only - closeBrowser is now stable (uses refs internally)
  useEffect(() => {
    // Return cleanup function
    return () => {
      // Only close on actual unmount, not re-renders
      // closeBrowser handles stopping streaming internally
      closeBrowser();
    };
    // Empty deps = only run cleanup on unmount
    // closeBrowser is stable (uses refs, no deps)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==========================================================================
  // Frame Rendering (Optimized with requestAnimationFrame)
  // ==========================================================================

  // Store latest frame for RAF to render
  useEffect(() => {
    if (currentFrame) {
      pendingFrameRef.current = currentFrame;
    }
  }, [currentFrame]);

  // RAF-based rendering loop for smoother display
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', {
      alpha: false, // Disable alpha for faster rendering
    });
    if (!ctx) return;

    // Create a NEW image for this effect instance to avoid race conditions
    // Each effect run gets its own image to prevent stale closures
    const img = new Image();
    let lastRenderedFrame: string | null = null;
    let isEffectActive = true; // Track if this effect instance is still active

    const renderFrame = () => {
      // Don't render if effect has been cleaned up
      if (!isEffectActive) return;

      const frame = pendingFrameRef.current;

      // Only render if we have a new frame
      if (frame && frame !== lastRenderedFrame) {
        lastRenderedFrame = frame;

        // Update frame metrics
        const now = performance.now();
        const metrics = frameMetricsRef.current;
        metrics.count++;
        if (metrics.lastTime) {
          const elapsed = now - metrics.lastTime;
          metrics.fps = metrics.fps * 0.9 + (1000 / elapsed) * 0.1;
        }
        metrics.lastTime = now;

        // Load and render the frame
        img.onload = () => {
          if (!isEffectActive) return; // Don't draw if effect is stale
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        };
        img.src = `data:image/jpeg;base64,${frame}`;
      }

      rafIdRef.current = requestAnimationFrame(renderFrame);
    };

    rafIdRef.current = requestAnimationFrame(renderFrame);

    return () => {
      isEffectActive = false; // Mark effect as inactive to prevent stale renders
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  // IMPORTANT: Include !!currentFrame in deps so effect re-runs when canvas becomes available
  // (canvas only exists when currentFrame is truthy - see conditional rendering below)
  }, [canvasSize.width, canvasSize.height, !!currentFrame]);

  // ==========================================================================
  // Input Handlers
  // ==========================================================================

  /**
   * Calculate browser coordinates from canvas coordinates.
   * Since we now sync viewport size to canvas size, the scale should be 1:1.
   * We use the canvas buffer dimensions (canvasSize) rather than session dimensions
   * to ensure accuracy even during viewport resize transitions.
   */
  const getBrowserCoordinates = useCallback(
    (clientX: number, clientY: number): { x: number; y: number } => {
      if (!canvasRef.current) {
        return { x: 0, y: 0 };
      }

      const rect = canvasRef.current.getBoundingClientRect();
      // With synchronized viewport sizing, canvas CSS size should match buffer size
      // Scale factor accounts for any slight differences during resize transitions
      const scaleX = canvasSize.width / rect.width;
      const scaleY = canvasSize.height / rect.height;

      return {
        x: Math.round((clientX - rect.left) * scaleX),
        y: Math.round((clientY - rect.top) * scaleY),
      };
    },
    [canvasSize]
  );

  /**
   * Handle mouse move (throttled for performance)
   */
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const now = performance.now();
      // Throttle mouse moves to ~60fps to avoid flooding the socket
      if (now - lastMouseMoveRef.current < MOUSE_MOVE_THROTTLE_MS) {
        return;
      }
      lastMouseMoveRef.current = now;

      const { x, y } = getBrowserCoordinates(e.clientX, e.clientY);
      sendMouseMove(x, y);
    },
    [getBrowserCoordinates, sendMouseMove]
  );

  /**
   * Handle mouse click - also ensures canvas has focus for keyboard input
   */
  const handleMouseClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      e.preventDefault();
      // Ensure canvas has focus for keyboard input
      canvasRef.current?.focus();
      const { x, y } = getBrowserCoordinates(e.clientX, e.clientY);
      const button = e.button === 2 ? 'right' : e.button === 1 ? 'middle' : 'left';
      sendMouseClick(x, y, button);
    },
    [getBrowserCoordinates, sendMouseClick]
  );

  /**
   * Handle scroll
   */
  const handleWheel = useCallback(
    (e: React.WheelEvent<HTMLCanvasElement>) => {
      e.preventDefault();
      sendScroll(e.deltaX, e.deltaY);
    },
    [sendScroll]
  );

  /**
   * Handle key down - uses sendType for printable characters for better text input
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLCanvasElement>) => {
      e.preventDefault();
      e.stopPropagation();

      const modifiers: ('Alt' | 'Control' | 'Meta' | 'Shift')[] = [];
      if (e.altKey) modifiers.push('Alt');
      if (e.ctrlKey) modifiers.push('Control');
      if (e.metaKey) modifiers.push('Meta');
      if (e.shiftKey) modifiers.push('Shift');

      // For printable characters without modifiers (except Shift), use sendType for reliability
      const isPrintable = e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey;

      if (isPrintable) {
        // Use type() for better text input handling
        sendType(e.key);
      } else {
        // Use keyDown for special keys (Enter, Tab, Backspace, arrows, etc.)
        sendKeyDown(e.key, modifiers.length > 0 ? modifiers : undefined);
      }
    },
    [sendKeyDown, sendType]
  );

  /**
   * Handle key up
   */
  const handleKeyUp = useCallback(
    (e: React.KeyboardEvent<HTMLCanvasElement>) => {
      e.preventDefault();
      sendKeyUp(e.key);
    },
    [sendKeyUp]
  );

  /**
   * Handle right-click context menu
   */
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Show custom context menu at click position
    setContextMenuPosition({ x: e.clientX, y: e.clientY });
    setIsContextMenuOpen(true);
  }, []);

  // ==========================================================================
  // Mobile Keyboard Bridge Handlers
  // ==========================================================================

  /**
   * Cancel any pending long press timer.
   * IMPORTANT: This must be defined BEFORE handleTouchStart to avoid TDZ errors
   * in minified production builds (useCallback creates a const that cannot be
   * accessed before initialization).
   */
  const cancelLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  /**
   * Handle touch start on canvas - focuses the hidden proxy input to summon
   * the mobile keyboard. MUST be called synchronously inside the user gesture
   * (iOS restriction: programmatic focus outside a tap will fail).
   *
   * Also starts a long-press timer for context menu on mobile.
   */
  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLCanvasElement>) => {
      if (!isMobile) return;

      // Get touch coordinates
      const touch = e.touches[0];
      if (!touch) return;

      // Store touch position for potential long-press context menu
      const touchX = touch.clientX;
      const touchY = touch.clientY;

      // Start long-press timer for context menu
      isLongPressRef.current = false;
      cancelLongPress();
      longPressTimerRef.current = setTimeout(() => {
        isLongPressRef.current = true;
        // Show context menu at touch position
        setContextMenuPosition({ x: touchX, y: touchY });
        setIsContextMenuOpen(true);
        // Vibrate to indicate long-press activated (if supported)
        if (navigator.vibrate) {
          navigator.vibrate(50);
        }
      }, LONG_PRESS_DURATION_MS);

      // Forward as a click to the remote browser (for short taps)
      const { x, y } = getBrowserCoordinates(touchX, touchY);
      sendMouseClick(x, y, 'left');

      // Focus the hidden input SYNCHRONOUSLY to summon mobile keyboard
      // This MUST happen inside the user gesture - no setTimeout/async allowed
      if (mobileKeyboardInputRef.current) {
        mobileKeyboardInputRef.current.focus();
        // Clear any previous value to get clean input
        mobileKeyboardInputRef.current.value = '';
        lastMobileInputRef.current = '';
      }
    },
    [isMobile, getBrowserCoordinates, sendMouseClick, cancelLongPress]
  );

  /**
   * Handle input from the hidden mobile keyboard proxy.
   * Captures typed characters and forwards them to Playwright via sendType().
   */
  const handleMobileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      const oldValue = lastMobileInputRef.current;

      // Detect what was typed by comparing old and new values
      if (newValue.length > oldValue.length) {
        // Characters were added - send the new characters
        const addedText = newValue.slice(oldValue.length);
        sendType(addedText);
      } else if (newValue.length < oldValue.length) {
        // Characters were deleted - send backspace(s)
        const deletedCount = oldValue.length - newValue.length;
        for (let i = 0; i < deletedCount; i++) {
          sendKeyDown('Backspace');
        }
      }

      lastMobileInputRef.current = newValue;
    },
    [sendType, sendKeyDown]
  );

  /**
   * Handle special keys (Enter, etc.) from the hidden mobile keyboard proxy.
   */
  const handleMobileKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        sendKeyDown('Enter');
        // Clear the input after Enter
        if (mobileKeyboardInputRef.current) {
          mobileKeyboardInputRef.current.value = '';
          lastMobileInputRef.current = '';
        }
      } else if (e.key === 'Tab') {
        e.preventDefault();
        sendKeyDown('Tab');
      }
      // Backspace is handled in handleMobileInput via value comparison
    },
    [sendKeyDown]
  );

  // ==========================================================================
  // Context Menu Handlers
  // ==========================================================================

  /**
   * Close the context menu
   */
  const closeContextMenu = useCallback(() => {
    setIsContextMenuOpen(false);
    setContextMenuPosition(null);
  }, []);

  /**
   * Copy action - gets selection from remote browser and copies to local clipboard
   */
  const handleCopy = useCallback(async () => {
    try {
      const result = await getSelection();
      if (result.success && result.text) {
        await navigator.clipboard.writeText(result.text);
      }
    } catch (err) {
      console.error('[RemoteBrowserPanel] Copy failed:', err);
    }
  }, [getSelection]);

  /**
   * Paste action - pastes from local clipboard to remote browser
   */
  const handlePaste = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        paste(text);
      }
    } catch (err) {
      console.error('[RemoteBrowserPanel] Paste failed:', err);
    }
  }, [paste]);

  /**
   * Select all action
   */
  const handleSelectAll = useCallback(() => {
    selectAll();
  }, [selectAll]);

  /**
   * Reload page action
   */
  const handleReload = useCallback(() => {
    reload();
  }, [reload]);

  /**
   * Handle touch end - cancel long press if finger lifted before threshold
   */
  const handleTouchEnd = useCallback(() => {
    cancelLongPress();
    // Reset long press flag after a short delay to allow click to be suppressed
    setTimeout(() => {
      isLongPressRef.current = false;
    }, 100);
  }, [cancelLongPress]);

  /**
   * Handle touch move - cancel long press if finger moves (user is scrolling)
   */
  const handleTouchMove = useCallback(() => {
    cancelLongPress();
  }, [cancelLongPress]);

  // ==========================================================================
  // Render
  // ==========================================================================

  // Connection states
  if (connectionState === 'disconnected' || connectionState === 'connecting') {
    return (
      <div
        className={`flex flex-col items-center justify-center h-full p-8 ${
          isDynamicsTheme ? 'bg-dark-surface' : 'bg-dark-bg'
        }`}
      >
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-500 border-t-transparent mb-4" />
        <p className={`text-sm ${isDynamicsTheme ? 'text-dark-muted' : 'text-dark-muted'}`}>
          {connectionState === 'connecting' ? 'Connecting to remote browser...' : 'Disconnected'}
        </p>
      </div>
    );
  }

  // Error state
  if (error && !session) {
    return (
      <div
        className={`flex flex-col items-center justify-center h-full p-8 text-center ${
          isDynamicsTheme ? 'bg-dark-surface' : 'bg-dark-bg'
        }`}
      >
        <svg
          className={`w-16 h-16 mb-4 ${isDynamicsTheme ? 'text-red-400' : 'text-red-500'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <h3 className={`text-lg font-medium mb-2 ${isDynamicsTheme ? 'text-white' : 'text-white'}`}>
          Unable to load remote browser
        </h3>
        <p className={`text-sm mb-4 ${isDynamicsTheme ? 'text-dark-muted' : 'text-dark-muted'}`}>
          {error}
        </p>
        <button
          type="button"
          onClick={() => openBrowser(url, viewport, profileId)}
          className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-all duration-150 min-h-[44px]"
        >
          Retry
        </button>
      </div>
    );
  }

  // Loading state (session exists but no frame yet)
  if (session && !currentFrame) {
    return (
      <div
        className={`flex flex-col items-center justify-center h-full p-8 ${
          isDynamicsTheme ? 'bg-dark-surface' : 'bg-dark-bg'
        }`}
      >
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-500 border-t-transparent mb-4" />
        <p className={`text-sm ${isDynamicsTheme ? 'text-dark-muted' : 'text-dark-muted'}`}>
          Loading {session.title || url}...
        </p>
      </div>
    );
  }

  // Main browser view
  return (
    <div
      ref={containerRef}
      className={`relative w-full h-full overflow-hidden ${
        isDynamicsTheme ? 'bg-dark-input' : 'bg-dark-surface'
      }`}
    >
      {/* Navigation bar */}
      <div
        className={`absolute top-0 left-0 right-0 z-10 px-2 py-1.5 flex items-center gap-2 text-xs ${
          isDynamicsTheme ? 'bg-dark-surface/50/90 text-dark-text' : 'bg-dark-input/90 text-dark-text'
        }`}
      >
        {/* Navigation buttons */}
        <div className="flex items-center gap-1">
          {/* Refresh button */}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={!session}
            className={`
              p-1.5 rounded transition-all duration-150
              ${isDynamicsTheme
                ? 'hover:bg-dark-surface/50 disabled:opacity-40'
                : 'hover:bg-dark-surface/40 disabled:opacity-40'
              }
            `}
            title="Refresh page"
            aria-label="Refresh page"
            data-testid="remote-browser-refresh-btn"
          >
            <svg
              className={`w-3.5 h-3.5 ${session?.isLoading ? 'animate-spin' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
        </div>

        {/* Connection indicator */}
        <div
          className={`w-2 h-2 rounded-full flex-shrink-0 ${
            connectionState === 'connected' ? 'bg-green-500' : 'bg-yellow-500'
          }`}
          title={connectionState}
          data-testid="remote-browser-connection-indicator"
        />

        {/* URL input */}
        <div className="flex-1 flex items-center min-w-0">
          <input
            ref={urlInputRef}
            type="text"
            value={urlInputValue}
            onChange={(e) => setUrlInputValue(e.target.value)}
            onKeyDown={handleUrlKeyDown}
            onFocus={handleUrlFocus}
            onBlur={handleUrlBlur}
            className={`
              w-full px-2 py-1 rounded text-xs font-mono
              outline-none transition-all duration-150
              ${isDynamicsTheme
                ? isEditingUrl
                  ? 'bg-dark-surface border border-brand-500 text-white'
                  : 'bg-dark-input border border-transparent text-dark-text hover:bg-dark-surface hover:border-dark-border/30'
                : isEditingUrl
                  ? 'bg-dark-surface border border-brand-500 text-white'
                  : 'bg-dark-surface/50/50 border border-transparent text-white/90 hover:bg-dark-surface/40 hover:border-dark-border/30'
              }
            `}
            placeholder="Enter URL and press Enter"
            aria-label="URL"
            data-testid="remote-browser-url-input"
          />
        </div>

        {/* Go button (shown when editing) */}
        {isEditingUrl && (
          <button
            type="button"
            onClick={handleNavigate}
            className={`
              px-2 py-1 rounded text-xs font-medium transition-all duration-150
              ${isDynamicsTheme
                ? 'bg-blue-500 text-white hover:bg-brand-600'
                : 'bg-brand-600 text-white hover:bg-brand-700'
              }
            `}
            title="Navigate to URL"
            aria-label="Go to URL"
            data-testid="remote-browser-go-btn"
          >
            Go
          </button>
        )}

        {/* Loading indicator */}
        {session?.isLoading && !isEditingUrl && (
          <div className="animate-spin rounded-full h-3 w-3 border-2 border-brand-500 border-t-transparent flex-shrink-0" />
        )}

        {/* Profile indicator */}
        {session?.profileId && !isEditingUrl && (
          <span
            className={`px-1.5 py-0.5 rounded text-xs flex-shrink-0 ${
              isDynamicsTheme ? 'bg-blue-100 text-blue-700' : 'bg-blue-900 text-blue-300'
            }`}
          >
            {session.profileId}
          </span>
        )}

        {/* FPS indicator */}
        {displayFps > 0 && !isEditingUrl && (
          <span
            className={`px-1.5 py-0.5 rounded text-xs flex-shrink-0 font-mono ${
              displayFps >= 12
                ? isDynamicsTheme ? 'bg-green-100 text-green-700' : 'bg-green-900 text-green-300'
                : displayFps >= 8
                  ? isDynamicsTheme ? 'bg-yellow-100 text-yellow-700' : 'bg-yellow-900 text-yellow-300'
                  : isDynamicsTheme ? 'bg-red-100 text-red-700' : 'bg-red-900 text-red-300'
            }`}
            title="Current frame rate"
          >
            {displayFps} FPS
          </span>
        )}
      </div>

      {/* Canvas */}
      {/* Canvas element for rendering screenshots.
          CRITICAL SIZING RULES:
          - The canvas width/height attributes set the internal resolution (pixel buffer)
          - The CSS width/height set the display size on screen
          - These MUST match to avoid stretching/squishing
          - DO NOT use CSS scale() transforms - they cause blurry rendering
          - The canvas matches the panel size exactly (minus status bar) */}
      <canvas
        ref={canvasRef}
        width={canvasSize.width}
        height={canvasSize.height}
        className={`absolute cursor-pointer outline-none ${
          isFocused ? 'ring-2 ring-brand-500 ring-inset' : ''
        }`}
        style={{
          // Position below status bar
          top: '28px',
          left: 0,
          // CSS size MUST match canvas buffer size to prevent stretching
          // Using explicit pixel values, NOT w-full/h-full which causes distortion
          width: `${canvasSize.width}px`,
          height: `${canvasSize.height}px`,
        }}
        tabIndex={0}
        onMouseMove={handleMouseMove}
        onClick={handleMouseClick}
        onMouseDown={handleMouseClick}
        onContextMenu={handleContextMenu}
        onWheel={handleWheel}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onFocus={handleFocus}
        onBlur={handleBlur}
        // Mobile keyboard bridge: touch handler to summon soft keyboard
        // Also handles long-press detection for context menu
        onTouchStart={isMobile ? handleTouchStart : undefined}
        onTouchEnd={isMobile ? handleTouchEnd : undefined}
        onTouchMove={isMobile ? handleTouchMove : undefined}
        onTouchCancel={isMobile ? handleTouchEnd : undefined}
        data-testid="remote-browser-canvas"
      />

      {/* Hidden Mobile Keyboard Proxy Input
          This invisible input exists solely to summon the mobile on-screen keyboard.
          When a user taps the canvas on a mobile device, we focus this input to trigger
          the keyboard, then forward all typed characters to the remote Playwright browser.
          This is a standard pattern used by remote desktops and browser isolation platforms. */}
      {isMobile && (
        <input
          ref={mobileKeyboardInputRef}
          type="text"
          aria-hidden="true"
          tabIndex={-1}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          onChange={handleMobileInput}
          onKeyDown={handleMobileKeyDown}
          style={{
            position: 'absolute',
            opacity: 0,
            width: 1,
            height: 1,
            padding: 0,
            margin: 0,
            border: 'none',
            pointerEvents: 'none',
            // Position it near the bottom where keyboards appear to minimize visual jump
            bottom: 0,
            left: '50%',
          }}
        />
      )}

      {/* Keyboard focus hint */}
      {!isFocused && session && currentFrame && (
        <div
          className={`absolute bottom-12 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-lg text-xs ${
            isDynamicsTheme ? 'bg-dark-surface/50 text-dark-muted' : 'bg-dark-input text-dark-text'
          }`}
        >
          {isMobile ? 'Tap to enable keyboard input' : 'Click to enable keyboard input'}
        </div>
      )}

      {/* Error overlay */}
      {error && session && (
        <div
          className={`absolute bottom-0 left-0 right-0 p-3 text-sm ${
            isDynamicsTheme ? 'bg-red-100 text-red-700' : 'bg-red-900/90 text-red-200'
          }`}
        >
          {error}
        </div>
      )}

      {/* Context Menu */}
      {contextMenuPosition && (
        <RemoteBrowserContextMenu
          position={contextMenuPosition}
          isOpen={isContextMenuOpen}
          onClose={closeContextMenu}
          onCopy={handleCopy}
          onPaste={handlePaste}
          onSelectAll={handleSelectAll}
          onReload={handleReload}
        />
      )}
    </div>
  );
}

export default RemoteBrowserPanel;
